import { useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import type { MediaPin, Shape } from './geometry';

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

interface Props {
  pins: MediaPin[];
  draft: Shape | null;
  selectedId: string | null;
  highlightId: string | null;
  commenting: boolean;
  onPlace: (shape: Shape) => void;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
  /** Click on the media itself while browsing (e.g. play/pause, deselect). */
  onBackgroundClick?: () => void;
  children?: ReactNode;
}

/**
 * Pin layer shared by the image and video canvases. Coordinates are fractions
 * of the media (0 to 1 from the top-left), so pins stay put at any zoom.
 * Click drops a pin; click-and-drag marks a region.
 */
export function MediaPins({ pins, draft, selectedId, highlightId, commenting, onPlace, onSelect, onHover, onBackgroundClick, children }: Props) {
  const layer = useRef<HTMLDivElement>(null);
  const start = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  const [drag, setDrag] = useState<Shape | null>(null);

  const toFraction = (event: ReactPointerEvent) => {
    const rect = layer.current!.getBoundingClientRect();
    return { x: clamp01((event.clientX - rect.left) / rect.width), y: clamp01((event.clientY - rect.top) / rect.height) };
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!commenting || event.button !== 0 || (event.target as HTMLElement).closest('[data-pin], .composer')) return;
    event.preventDefault();
    const point = toFraction(event);
    start.current = { ...point, px: event.clientX, py: event.clientY };
    layer.current?.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const origin = start.current;
    if (!origin) return;
    if (!drag && Math.hypot(event.clientX - origin.px, event.clientY - origin.py) < 6) return;
    const point = toFraction(event);
    setDrag({ x: Math.min(origin.x, point.x), y: Math.min(origin.y, point.y), width: Math.abs(point.x - origin.x), height: Math.abs(point.y - origin.y) });
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const origin = start.current;
    if (!origin) return;
    start.current = null;
    layer.current?.releasePointerCapture?.(event.pointerId);
    const region = drag;
    setDrag(null);
    if (region && (region.width ?? 0) > 0.01 && (region.height ?? 0) > 0.01) onPlace(region);
    else onPlace({ x: origin.x, y: origin.y });
  };

  const visibleDraft = drag || draft;

  return (
    <div
      ref={layer}
      className="media-layer"
      style={{ position: 'absolute', inset: 0, touchAction: commenting ? 'none' : undefined }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        start.current = null;
        setDrag(null);
      }}
      onClick={(event) => {
        if (!commenting && event.target === event.currentTarget) onBackgroundClick?.();
      }}
    >
      {pins.map((pin) => {
        const active = pin.id === selectedId;
        const state = `${active ? 'is-active' : ''} ${pin.id === highlightId ? 'is-highlighted' : ''} s-${pin.status}`;
        return (
          <div key={pin.id}>
            {pin.width !== undefined && pin.height !== undefined && (
              <div className={`media-region ${state}`} style={{ left: `${pin.x * 100}%`, top: `${pin.y * 100}%`, width: `${pin.width * 100}%`, height: `${pin.height * 100}%` }} />
            )}
            <button
              type="button"
              data-pin
              className={`media-pin ${state}`}
              style={{ left: `${pin.x * 100}%`, top: `${pin.y * 100}%` }}
              aria-label={`Revision ${pin.number}`}
              aria-pressed={active}
              onClick={(event) => {
                event.stopPropagation();
                onSelect(pin.id);
              }}
              onMouseEnter={() => onHover(pin.id)}
              onMouseLeave={() => onHover(null)}
            >
              {pin.number}
            </button>
          </div>
        );
      })}

      {visibleDraft && (
        <>
          {visibleDraft.width !== undefined && visibleDraft.height !== undefined && (
            <div
              className="media-region is-draft"
              style={{ left: `${visibleDraft.x * 100}%`, top: `${visibleDraft.y * 100}%`, width: `${visibleDraft.width * 100}%`, height: `${visibleDraft.height * 100}%` }}
            />
          )}
          {!drag && (
            <span className="media-pin is-draft" style={{ left: `${visibleDraft.x * 100}%`, top: `${visibleDraft.y * 100}%` }} aria-hidden="true">
              +
            </span>
          )}
        </>
      )}
      {children}
    </div>
  );
}
